import type { SceneNodeId } from '../../scene/sceneState.js';

export type SceneTreeVirtualTarget = 'scene' | 'component';

export interface SceneTreeSelectionController {
  getSelectedSceneNodeId(): SceneNodeId | null;
  getSelectedSceneNodeComponentId(): string | null;
  setSelectedSceneNodeId(id: SceneNodeId | null, componentId?: string | null): void;
  getSelectedVirtualTarget(): SceneTreeVirtualTarget | null;
  setSelectedVirtualTarget(target: SceneTreeVirtualTarget | null): void;
  selectSceneNode(id: SceneNodeId | null, componentId?: string | null): void;
  selectVirtualTarget(target: SceneTreeVirtualTarget): void;
  isFrameCollapsed(nodeId: SceneNodeId): boolean;
  toggleFrameCollapsed(nodeId: SceneNodeId): void;
}

export function createSceneTreeSelectionController(
  onSelectionChange?: () => void,
  getActiveComponentId?: () => string | null,
): SceneTreeSelectionController {
  const collapsedFrames = new Set<SceneNodeId>();
  let selectedSceneNodeId: SceneNodeId | null = null;
  let selectedSceneNodeComponentId: string | null = null;
  let selectedVirtualTarget: SceneTreeVirtualTarget | null = null;

  function notifySelectionChange(): void {
    onSelectionChange?.();
  }

  function normalizeComponentId(componentId?: string | null): string | null {
    return componentId === undefined ? (getActiveComponentId?.() ?? null) : componentId;
  }

  return {
    getSelectedSceneNodeId(): SceneNodeId | null {
      return selectedSceneNodeId;
    },
    getSelectedSceneNodeComponentId(): string | null {
      return selectedSceneNodeComponentId;
    },
    setSelectedSceneNodeId(id: SceneNodeId | null, componentId?: string | null): void {
      const ownerComponentId = id ? normalizeComponentId(componentId) : null;
      if (selectedSceneNodeId === id && selectedSceneNodeComponentId === ownerComponentId) return;
      selectedSceneNodeId = id;
      selectedSceneNodeComponentId = ownerComponentId;
      notifySelectionChange();
    },
    getSelectedVirtualTarget(): SceneTreeVirtualTarget | null {
      return selectedVirtualTarget;
    },
    setSelectedVirtualTarget(target: SceneTreeVirtualTarget | null): void {
      if (selectedVirtualTarget === target) return;
      selectedVirtualTarget = target;
      notifySelectionChange();
    },
    selectSceneNode(id: SceneNodeId | null, componentId?: string | null): void {
      const ownerComponentId = id ? normalizeComponentId(componentId) : null;
      if (
        selectedVirtualTarget === null
        && selectedSceneNodeId === id
        && selectedSceneNodeComponentId === ownerComponentId
      ) return;
      selectedVirtualTarget = null;
      selectedSceneNodeId = id;
      selectedSceneNodeComponentId = ownerComponentId;
      notifySelectionChange();
    },
    selectVirtualTarget(target: SceneTreeVirtualTarget): void {
      if (selectedVirtualTarget === target && selectedSceneNodeId === null) return;
      selectedVirtualTarget = target;
      selectedSceneNodeId = null;
      selectedSceneNodeComponentId = null;
      notifySelectionChange();
    },
    isFrameCollapsed(nodeId: SceneNodeId): boolean {
      return collapsedFrames.has(nodeId);
    },
    toggleFrameCollapsed(nodeId: SceneNodeId): void {
      if (collapsedFrames.has(nodeId)) {
        collapsedFrames.delete(nodeId);
      } else {
        collapsedFrames.add(nodeId);
      }
    },
  };
}
